
import { useState, useRef, useEffect, useCallback } from 'react';
import { WeeklyMenu, Recipe, ShoppingList } from '@/types';
import { UseShoppingListReturn } from './useShoppingList/types';
import { shoppingListStorage } from './useShoppingList/storage';
import { generateShoppingListFromMenu, preserveCheckedStatus } from './useShoppingList/generator';

export const useShoppingList = (userId: string | undefined): UseShoppingListReturn => {
  const [shoppingList, setShoppingList] = useState<ShoppingList | null>(null);
  const shoppingListGeneratedRef = useRef<boolean>(false);
  const initialLoadDone = useRef<boolean>(false);
  
  // Simplified loadShoppingList function
  const loadShoppingList = useCallback(() => {
    if (!userId || initialLoadDone.current) return;
    
    const storedList = shoppingListStorage.loadFromStorage(userId);
    if (storedList) {
      setShoppingList(storedList);
      console.log('Shopping list loaded from localStorage:', storedList);
      shoppingListGeneratedRef.current = true;
    } else {
      console.log('No shopping list in localStorage, creating empty list');
      setShoppingList({ items: {} });
    }
    
    initialLoadDone.current = true;
  }, [userId]);
  
  // Load shopping list when component mounts
  useEffect(() => {
    if (userId) {
      loadShoppingList();
    }
  }, [userId, loadShoppingList]);

  // Generate shopping list function
  const generateShoppingList = useCallback(async (weeklyMenu: WeeklyMenu | null, recipes: Recipe[] | null) => {
    if (!weeklyMenu || !recipes || !userId) {
      console.log('Missing data for shopping list generation:', { hasMenu: !!weeklyMenu, hasRecipes: !!recipes, hasUserId: !!userId });
      return null;
    }
    
    try {
      console.log('🛒 Starting shopping list generation with menu:', weeklyMenu);
      console.log('📚 Available recipes:', recipes?.length || 0);
      
      const combinedIngredients = generateShoppingListFromMenu(weeklyMenu, recipes);
      
      // Preserve checked status from existing shopping list
      const ingredientsWithCheckedStatus = preserveCheckedStatus(combinedIngredients, shoppingList);
      
      const newShoppingList = { items: ingredientsWithCheckedStatus };
      setShoppingList(newShoppingList);
      
      shoppingListStorage.saveToStorage(userId, newShoppingList);
      
      console.log('✅ Shopping list generated successfully:', {
        totalItems: Object.keys(combinedIngredients).length,
        items: Object.keys(combinedIngredients)
      });
      
      shoppingListGeneratedRef.current = true;
      return newShoppingList;
    } catch (error) {
      console.error('❌ Error generating shopping list:', error);
      return null;
    }
  }, [userId, shoppingList]);

  const toggleShoppingItem = async (itemId: string): Promise<void> => {
    if (!shoppingList || !userId) return Promise.resolve();
    
    try {
      const updatedList = { ...shoppingList };
      if (updatedList.items[itemId]) {
        updatedList.items[itemId].checked = !updatedList.items[itemId].checked;
        setShoppingList(updatedList);
        
        shoppingListStorage.saveToStorage(userId, updatedList);
      }
      return Promise.resolve();
    } catch (error) {
      console.error('Error toggling shopping item:', error);
      return Promise.reject(error);
    }
  };
  
  const resetShoppingList = useCallback(() => {
    if (!userId) return;
    
    console.log('Resetting shopping list for user:', userId);
    shoppingListStorage.removeFromStorage(userId);
    setShoppingList({ items: {} });
    shoppingListGeneratedRef.current = false;
    initialLoadDone.current = false;
  }, [userId]);
  
  return {
    shoppingList,
    setShoppingList,
    loadShoppingList,
    generateShoppingList,
    toggleShoppingItem,
    resetShoppingList,
    shoppingListGeneratedRef
  };
};

// Re-export types for compatibility
export type { UseShoppingListReturn } from './useShoppingList/types';
